// src/sectors/pilates/sessions/components/calendar/MobileDayList/parts/SessionActionSheet.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { hm } from '../mobileDayList.utils';

function ActionRow({
  icon,
  title,
  sub,
  tone = 'info',
  onClick,
  disabled,
  endIcon = 'chevron_right',
}) {
  return (
    <MDBox
      onClick={disabled ? undefined : onClick}
      sx={(t) => {
        const isDark = t.palette.mode === 'dark';
        const c =
          tone === 'danger'
            ? t.palette.error.main
            : tone === 'success'
            ? t.palette.success.main
            : t.palette.info.main;

        const border = `1px solid ${alpha(t.palette.text.primary, isDark ? 0.14 : 0.1)}`;
        const bg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.92)';

        return {
          borderRadius: '16px',
          border,
          background: bg,
          p: 1.05,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          transition: 'transform .14s ease, box-shadow .14s ease, background .14s ease',
          boxShadow: `0 14px 28px ${alpha('#111827', isDark ? 0.22 : 0.08)}`,
          '&:hover': disabled
            ? {}
            : {
                transform: 'translateY(-1px)',
                boxShadow: `0 18px 34px ${alpha(c, isDark ? 0.16 : 0.12)}`,
              },
          '&:active': disabled ? {} : { transform: 'scale(0.996)' },
          minWidth: 0,
        };
      }}
    >
      <MDBox
        sx={(t) => {
          const isDark = t.palette.mode === 'dark';
          const c =
            tone === 'danger'
              ? t.palette.error.main
              : tone === 'success'
              ? t.palette.success.main
              : t.palette.info.main;

          const border = `1px solid ${alpha(t.palette.text.primary, isDark ? 0.14 : 0.1)}`;

          return {
            width: 38,
            height: 38,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            border,
            background: alpha(c, isDark ? 0.14 : 0.1),
            flex: '0 0 auto',
          };
        }}
      >
        <Icon
          sx={(t) => ({
            fontSize: 18,
            color:
              tone === 'danger'
                ? t.palette.error.main
                : tone === 'success'
                ? t.palette.success.main
                : t.palette.info.main,
          })}
        >
          {icon}
        </Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.12 }}>
        <MDTypography variant="button" sx={{ fontWeight: 1000, lineHeight: 1.1 }} noWrap>
          {title}
        </MDTypography>
        {sub ? (
          <MDTypography variant="caption" sx={{ opacity: 0.78, fontWeight: 800 }} noWrap>
            {sub}
          </MDTypography>
        ) : null}
      </MDBox>

      <MDBox sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Icon sx={{ opacity: 0.55, fontSize: 20 }}>{endIcon}</Icon>
      </MDBox>
    </MDBox>
  );
}

export default function SessionActionSheet({
  open,
  onClose,
  session,
  busy,
  onEdit,
  onMovePick,
  onCopyPick,
  onDelete,
}) {
  const subtitle = session?.dayISO ? `${session.dayISO} • ${hm(session?.startMin)}` : '—';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: (t) => ({
          borderRadius: '22px',
          overflow: 'hidden',
          border: `1px solid ${alpha(
            t.palette.text.primary,
            t.palette.mode === 'dark' ? 0.16 : 0.12
          )}`,
          background: t.palette.mode === 'dark' ? alpha('#0b1220', 0.92) : alpha('#fff', 0.96),
          backdropFilter: 'blur(10px)',
          boxShadow: `0 26px 70px ${alpha('#000', t.palette.mode === 'dark' ? 0.55 : 0.18)}`,
        }),
      }}
    >
      <DialogTitle sx={{ pb: 1.1 }}>
        <MDBox
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}
        >
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <MDBox
              sx={(t) => ({
                width: 42,
                height: 42,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                border: `1px solid ${alpha(t.palette.info.main, 0.22)}`,
                background: alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.16 : 0.12),
                flex: '0 0 auto',
              })}
            >
              <Icon sx={{ opacity: 0.9 }}>auto_awesome</Icon>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography component="div" variant="h6" sx={{ fontWeight: 1100, lineHeight: 1.1 }}>
                Seans İşlemleri
              </MDTypography>
              <MDTypography
                component="div"
                variant="caption"
                sx={{ opacity: 0.75, fontWeight: 900 }}
                noWrap
              >
                {subtitle}
              </MDTypography>
            </MDBox>
          </MDBox>

          <IconButton
            size="small"
            onClick={onClose}
            disabled={busy}
            sx={(t) => ({
              borderRadius: '14px',
              border: `1px solid ${alpha(
                t.palette.text.primary,
                t.palette.mode === 'dark' ? 0.16 : 0.12
              )}`,
              background:
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            })}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 1.2 }}>
        <MDBox sx={{ display: 'grid', gap: 0.85 }}>
          <ActionRow
            icon="edit"
            title="Düzenle"
            sub="Seans bilgilerini güncelle"
            tone="info"
            onClick={onEdit}
            disabled={busy}
          />
          <ActionRow
            icon="open_with"
            title="Taşı"
            sub="Hedef gün ve saat seç"
            tone="success"
            onClick={onMovePick}
            disabled={busy}
            endIcon="north_east"
          />
          <ActionRow
            icon="content_copy"
            title="Kopyala"
            sub="Hedef gün ve saat seç"
            tone="info"
            onClick={onCopyPick}
            disabled={busy}
            endIcon="north_east"
          />

          <MDBox sx={{ pt: 0.25 }}>
            <Divider />
          </MDBox>

          <ActionRow
            icon="delete_outline"
            title="Sil"
            sub="Seansı tamamen kaldır"
            tone="danger"
            onClick={onDelete}
            disabled={busy}
            endIcon="warning"
          />
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.6 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
          disabled={busy}
          fullWidth
        >
          Kapat
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
